import React, { useContext, useEffect, useMemo, useState } from 'react'
import { EPCRContext } from '../EPCR'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Grid,
  TextField,
  FormHelperText,
  Box,
} from '@mui/material'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import Transport from './Transport'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NextButton from '../NextButton'
import { TenantSettingsContext } from '../../../../App'
import AnTimeField from '../../../../components/DateTime/TimeField'
import PaediatricCO from './PaediatricCO'
import Signature from './Signature'
import Safeguarding from '../Safeguarding/Safeguarding'
import API from '../../../../services/API'
import { useNavigate } from 'react-router-dom'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { regionLabels } from '../../../../helpers/region'

const getLinkedEventId = (linkedEvent) => {
  if (!linkedEvent) return null
  return linkedEvent._id || linkedEvent
}

const TreatmentCentreTransfer = ({ formValues }) => {
  const navigate = useNavigate()
  const { setSiteMode, setEpcrPracticeMode } = useContext(SiteModeContext)
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState('')
  const [loading, setLoading] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)

  const linkedEventID = getLinkedEventId(formValues?.incident?.linkedEvent)
  const currentTreatmentLocation =
    formValues?.review?.currentTreatmentLocation?._id ||
    formValues?.review?.currentTreatmentLocation

  const selectedLocationDetails = useMemo(
    () => locations.find((location) => location._id === selectedLocation),
    [locations, selectedLocation],
  )

  useEffect(() => {
    let active = true

    const loadLocations = async () => {
      if (!linkedEventID) {
        setLocations([])
        setSelectedLocation('')
        return
      }

      setLoading(true)

      try {
        const res = await API.get(
          `/commandcontrol/treatment-centres/event/${linkedEventID}`,
        )

        if (active && res.status === 200) {
          setLocations(res.data || [])
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    loadLocations()

    return () => {
      active = false
    }
  }, [linkedEventID])

  if (!linkedEventID) return null

  const handleTransfer = async () => {
    if (!selectedLocation || transferring) return

    setTransferring(true)

    try {
      const res = await API.post('/epcr/transfer-treatment-location', {
        epcrID: formValues._id,
        treatmentLocation: selectedLocation,
        reason: 'Transferred to event treatment centre from ePCR plan screen',
      })

      if (res.status === 200) {
        setTransferComplete(true)
        setConfirmOpen(false)
        setSiteMode('normal')
        setEpcrPracticeMode(false)
        navigate('/epcr')
      }
    } finally {
      setTransferring(false)
    }
  }

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(35,154,164,0.24)',
          backgroundColor: '#F8FEFF',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Typography fontWeight={900}>Treatment Centre Transfer</Typography>
              <Typography variant="body2" color="text.secondary">
                Send this ePCR to an event treatment centre while keeping the
                final patient outcome separate.
              </Typography>
            </Box>

            {currentTreatmentLocation && (
              <Chip
                label="Already assigned to a treatment centre"
                color="success"
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            )}
          </Stack>

          {transferComplete && (
            <Alert severity="success">
              ePCR transferred to the treatment centre.
            </Alert>
          )}

          {locations.length === 0 && !loading ? (
            <Alert severity="info">
              There are no active treatment centres for the selected event.
            </Alert>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <FormControl fullWidth disabled={loading || locations.length === 0}>
                <InputLabel>Treatment Centre</InputLabel>
                <Select
                  value={selectedLocation}
                  label="Treatment Centre"
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location._id} value={location._id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {loading && (
                  <FormHelperText>Loading treatment centres...</FormHelperText>
                )}
              </FormControl>

              <Button
                variant="contained"
                disabled={!selectedLocation || transferring}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  minWidth: 180,
                  borderRadius: 999,
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                Transfer ePCR
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Transfer ePCR?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography>
              This will transfer the ePCR to{' '}
              <strong>{selectedLocationDetails?.name}</strong> and make it
              available on the treatment centre bed board.
            </Typography>
            <Alert severity="warning">
              A snapshot of the current ePCR will be saved for audit before the
              transfer is completed.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={transferring}
          >
            {transferring ? 'Transferring...' : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

const Plan = () => {
  const { formValues, handleChange } = useContext(EPCRContext)
  const tenantSettings = useContext(TenantSettingsContext)
  const { orgSettings } = tenantSettings.orgConfig
  const labels = regionLabels(tenantSettings.tenantInfo?.region)

  return (
    <Stack gap={2} width="100%">
      <EPCRCard>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title2">Plan</Typography>
          </Grid>
          <Grid item xs={12} lg={4}>
            <FormControl fullWidth>
              <InputLabel>Patient Outcome</InputLabel>
              <Select
                value={formValues?.plan?.ptOutcome ?? ''}
                onChange={(e) =>
                  handleChange({
                    moduleName: 'plan',
                    field: 'ptOutcome',
                    value: e.target.value,
                  })
                }
                label="Patient Outcome"
              >
                <MenuItem
                  value={`Transported to Hospital by ${orgSettings.orgShortName}`}
                >
                  Transported to Hospital by {orgSettings?.orgShortName}
                </MenuItem>
                <MenuItem value="Discharged following Treatment">
                  Discharged following Treatment
                </MenuItem>
                <MenuItem value="Discharged - Advice Only">
                  Discharged - Advice Only
                </MenuItem>
                <MenuItem value="Left before Treatment">
                  Left before Treatment
                </MenuItem>
                <MenuItem value="Handed over to Ambulance Crew">
                  Handed over to Ambulance Crew
                </MenuItem>
                <MenuItem value="Patient Deceased">Patient Deceased</MenuItem>
                <MenuItem value="Refused Treatment or Transport">
                  Refused Treatment or Transport
                </MenuItem>
                <MenuItem value="A&E - Own Transport">
                  A&E - Other Transport
                </MenuItem>
                {formValues?.incident?.incidentType !== 'Event' && <MenuItem value='Planned Transport Completed'>Planned Transport Completed</MenuItem>}
                <MenuItem value="Referred - Other">Referred - Other</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <PaediatricCO />
          {formValues?.plan?.ptOutcome === 'Handed over to Ambulance Crew' && (
            <>
              <Grid item xs={12} lg={8}>
                <SimpleTextField
                  value={formValues?.plan?.ambulanceHandoverTrust}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'plan',
                      field: 'ambulanceHandoverTrust',
                      value: e.target.value,
                    })
                  }
                  label={labels.ambulanceOrgLabel}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <SimpleTextField
                  value={formValues?.plan?.ambulanceHandoverCallsign}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'plan',
                      field: 'ambulanceHandoverCallsign',
                      value: e.target.value,
                    })
                  }
                  label="Ambulance Crew Callsign"
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <SimpleTextField
                  value={formValues?.plan?.ambulanceHandoverCAD}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'plan',
                      field: 'ambulanceHandoverCAD',
                      value: e.target.value,
                    })
                  }
                  label="CAD Number"
                />
              </Grid>
              <Grid item xs={6} lg={4}>
                <AnTimeField
                  time={formValues?.incident?.patientHandoverTime}
                  label="Handover Time"
                  onChange={(newTime) =>
                    handleChange({
                      moduleName: 'incident',
                      field: 'patientHandoverTime',
                      value: newTime,
                    })
                  }
                />
              </Grid>
            </>
          )}
          {formValues?.plan?.ptOutcome ===
            `Transported to Hospital by ${orgSettings.orgShortName}` && (
            <Grid item xs={12} lg={4}>
              <SimpleTextField
                value={formValues?.plan?.destinationName}
                onChange={(e) =>
                  handleChange({
                    moduleName: 'plan',
                    field: 'destinationName',
                    value: e.target.value,
                  })
                }
                label="Hospital Name"
              />
            </Grid>
          )}
          {formValues?.plan?.ptOutcome ===
            `Transported to Hospital by ${orgSettings.orgShortName}` && (
            <Grid item xs={12} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Destination Type</InputLabel>
                <Select
                  value={formValues?.plan?.destinationType ?? ''}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'plan',
                      field: 'destinationType',
                      value: e.target.value,
                    })
                  }
                  label="Destination Type"
                >
                  <MenuItem value="Emergency Department">
                    Emergency Department
                  </MenuItem>
                  <MenuItem value="Event Medical Centre">
                    Event Medical Centre
                  </MenuItem>
                  <MenuItem value="PPCI">PPCI</MenuItem>
                  <MenuItem value="Delivery Suite">Delivery Suite</MenuItem>
                  <MenuItem value="Stroke Unit">Stroke Unit</MenuItem>
                  <MenuItem value="Minor Injury Unit">
                    Minor Injury Unit
                  </MenuItem>
                  <MenuItem value="Trauma Unit">Trauma Unit</MenuItem>
                  <MenuItem value="Paediatrics">Paediatrics</MenuItem>
                  <MenuItem value="Paediatric MTC">Paediatric MTC</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {formValues?.plan?.ptOutcome === 'Refused Treatment or Transport' && (
            <Grid item xs={12}>
              <EPCRCard borderColor="purple">
                <Stack>
                  <Typography variant="title3">Patient Signature</Typography>
                  <Typography variant="caption">
                    I confirm that I have been offered appropriate assessment,
                    treatment, and/or transport by the attending medical team.
                    The risks of refusing this care have been explained to me,
                    and I understand that my condition could worsen.
                  </Typography>
                  <Typography variant="caption">
                    I accept responsibility for this decision and release the
                    attending crew and their organisation from liability.
                  </Typography>
                  <Typography variant="caption" fontStyle="italic">
                    This declaration may be signed by the patient, or by a
                    parent, guardian, or carer if applicable.
                  </Typography>
                  {formValues?.plan?.refusalSignature ? (
                    <Box
                      component="img"
                      src={formValues.plan.refusalSignature}
                      height={200}
                      sx={{
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Signature
                      moduleName="plan"
                      field="refusalSignature"
                      title="Patient Signature"
                    />
                  )}
                </Stack>
              </EPCRCard>
            </Grid>
          )}
          <TreatmentCentreTransfer formValues={formValues} />
          <Grid item xs={12}>
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={formValues?.plan?.planNotes ?? ''}
              onChange={(e) =>
                handleChange({
                  moduleName: 'plan',
                  field: 'planNotes',
                  value: e.target.value,
                })
              }
              label="Plan Notes"
            />
          </Grid>
        </Grid>
      </EPCRCard>
      {formValues?.plan?.ptOutcome?.startsWith('Transported to Hospital') && (
        <Transport formValues={formValues} handleChange={handleChange} />
      )}
      {/* Prompt until explicitly answered "no concern"; an answered "yes"
          keeps the card so the detail/referral fields stay reachable */}
      {formValues?.safeguarding?.concerns !== false && <Safeguarding />}
      <EPCRCard>
        <Grid container>
          <Grid item xs={12}>
            <TextField
              label="Advice Given"
              multiline
              fullWidth
              minRows={2}
              value={formValues?.plan?.adviceGiven}
              onChange={(e) =>
                handleChange({
                  moduleName: 'plan',
                  field: 'adviceGiven',
                  value: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </EPCRCard>
      <NextButton newModule="Sharing" />
    </Stack>
  )
}

export default Plan
